

import { Component, Prop } from 'vue-property-decorator';
import ErrorMessageHandlerMixin from '../../misc/ErrorMessageHandler.mixin';
import { mixins } from 'vue-class-component';
import { email, integer, minValue, required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { namespace } from 'vuex-class';
import Promoter from '@/models/Promoter';
import { promoterStoreActions } from '@/store/promoter.store';
import { Country } from '@/misc/allCountries';

const PromoterStore = namespace('promoter');

@Component({
    mixins: [validationMixin],
    validations: {
        phoneNumber: { required },
        promoterCopy: {
            firstName: {
                required
            },
            lastName: {
                required
            },
            mail: {
                required,
                email
            },
            activationThreshold: {
                required,
                integer,
                minValue: minValue(0)
            }
        }
    },
    components: {
        CountyCodeComponent: () => import(
            /* webpackChunkName: "CountyCodeComponent" */
            '@/components/CountyCode.component.vue')
    }
})
export default class EditPromoterComponent extends mixins(ErrorMessageHandlerMixin) {
  @Prop({ required: false, default: null })
  public promoter!: Promoter | null;

  @PromoterStore.Action(promoterStoreActions.CREATE)
  public createPromoterAction!: (promoter: Promoter) => Promise<Promoter>;

  @PromoterStore.Action(promoterStoreActions.UPDATE)
  public updatePromoterAction!: (manager: Promoter) => Promise<Promoter>;

  public promoterCopy: Promoter = new Promoter();
  public isLoading: boolean = false;
  private prefix: string = '+264';
  private phoneNumber: string = '';

  private onSelect(payload: Country): void {
      if (!payload) return;
      this.prefix = `+${payload.dialCode}`;
  }

  /**
   * The threshold is bound to a text field, so it arrives as a string. The API
   * rejects a non-number, and an empty field has to fall back to the default
   * rather than being sent as NaN.
   */
  private get parsedActivationThreshold(): number {
      const parsed = parseInt(this.promoterCopy.activationThreshold as unknown as string, 10);
      return Number.isNaN(parsed) ? 10 : parsed;
  }

  private created() {
      this.promoterCopy = Promoter.parseFromObject({ ...this.promoter });
      // A promoter created before this field existed comes back without it.
      if (this.promoterCopy.activationThreshold === undefined ||
          this.promoterCopy.activationThreshold === null) {
          this.promoterCopy.activationThreshold = 10;
      }

      if (this.promoter) {
          if (this.promoter.phone.prefix) this.prefix = this.promoter.phone.prefix;
          if (this.promoter.phone.phoneNumber) this.phoneNumber = this.promoter.phone.phoneNumber;
      }
  }

  public async saveData() {
      this.$v.$touch();
      if (this.$v.$invalid) {
          return;
      }

      if (this.promoter?.id) {
          await this.updatePromoter();
      } else {
          await this.createPromoter();
      }
  }

  private async createPromoter() {
      const promoterToUse = Promoter.parseFromObject({
          ...this.promoterCopy,
          phone: {
              prefix: this.prefix,
              phoneNumber: this.phoneNumber
          },
          activationThreshold: this.parsedActivationThreshold
      });

      try {
          await this.createPromoterAction(promoterToUse);
          this.dismiss(true);
          this.$notifySuccessSimplified('PROMOTER.CREATE_SUCCESS');
      } catch (e) {
          this.$notifyErrorSimplified('PROMOTER.CREATE_ERROR');
      } finally {
          this.isLoading = false;
      }
  }

  private async updatePromoter() {
      const promoterToUse = Promoter.parseFromObject({
          ...this.promoterCopy,
          phone: {
              prefix: this.prefix,
              phoneNumber: this.phoneNumber
          },
          activationThreshold: this.parsedActivationThreshold
      });

      try {
          await this.updatePromoterAction(promoterToUse);
          this.dismiss(true);
          this.$notifySuccessSimplified('PROMOTER.EDIT_SUCCESS');
      } catch (_) {
          this.$notifyErrorSimplified('PROMOTER.EDIT_ERROR');
      } finally {
          this.isLoading = false;
      }
  }

  public dismiss(reloadData: boolean = false) {
      this.$emit('closeDialog', reloadData);
  }
}

